/* eslint-disable multiline-ternary */
/* eslint-disable no-debugger */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useEffect,
  useState,
  useContext,
  type ReactElement
} from 'react'
import {
  Box,
  Button,
  Flex,
  Heading,
  Link,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text,
  UnorderedList
} from '@chakra-ui/react'
import { connection, RoomCode } from '../../renderer/roomConnector'
import { RoomSettingsContext } from '../+RoomSettingsContext'
import { ErrorModal, ScreenSizeErrorModal } from '../../components/Modals'
import { DeleteIcon, CheckIcon, ArrowUpIcon } from '@chakra-ui/icons'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import { activities, universalRoomOptions } from '../../activities'
import SideNav from '../../components/SideNav'

interface itemData {
  id: string
  name: string
  question: string
  votes: string[]
  archived: boolean
  deleted: boolean
}

const starterQuestions = [
  {
    id: 'abc',
    name: 'Seve',
    question: 'Example First Question',
    votes: [],
    archived: true,
    deleted: false
  },
  {
    id: 'cde',
    name: 'Seve',
    question: 'Example Second Question',
    votes: ['AAA', 'BBB', 'CCC', 'DDD'],
    archived: false,
    deleted: false
  },
  {
    id: 'fgh',
    name: 'Seve',
    question: 'Example Third Question',
    votes: [],
    archived: false,
    deleted: false
  }
]

const initialRoomSettings = {
  activity: activities.upForDiscussion,
  info: (
    <>
      <Heading size="sm" mb="0">
        Host
      </Heading>
      <UnorderedList>
        {activities.upForDiscussion.hostInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`hostInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
      <Heading size="sm" mb="0" mt="20px">
        Audience
      </Heading>
      <UnorderedList>
        {activities.upForDiscussion.audienceInfo.map(
          (text: string, index: number) => {
            return <ListItem key={`audienceInfo-${index}`}>{text}</ListItem>
          }
        )}
      </UnorderedList>
    </>
  ),
  options: {
    ...universalRoomOptions,
    hideAnsweredQuestions: {
      label: 'Hide Answered Questions',
      type: 'bool',
      value: false
    },
    hideQuestionName: {
      label: "Hide Question Asker's Name",
      type: 'bool',
      value: false
    },
    hideVoteNames: {
      label: 'Hide Vote Names',
      type: 'bool',
      value: false
    },
    showDeletedQuestions: {
      label: 'Show Deleted Questions',
      type: 'bool',
      value: false
    }
  }
}

let CHANNEL_GLOBAL: any = null

const Page: React.FC = () => {
  const [roomCode, setRoomCode] = useState<string>('')
  const { roomSettings, setRoomSettings } = useContext(RoomSettingsContext)
  const [preventRefresh, setPreventRefresh] = useState<boolean>(false)
  const [triggerRefresh, setTriggerRefresh] = useState<boolean>(false)

  const [isConnecting, setIsConnecting] = useState<boolean>(false)
  // eslint-disable-next-line prettier/prettier
  const [featuredQuestionIndex, setFeaturedQuestionIndex] = useState<
    number | null
  >(null)
  const [questions, setQuestions] = useState<itemData[]>(
    import.meta.env.MODE === 'development' ? starterQuestions : []
  )
  const [csvData, setCsvData] = useState<string[][]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [parent] = useAutoAnimate()

  const refreshList = (): void => {
    CHANNEL_GLOBAL.send({
      action: 'updateQuestionList',
      questions
    })
  }

  const updateItems = (
    message: {
      id: string
      clientId: string
      question: string
      type: string
      voteId?: string
      voteType?: string
    },
    channel: any
  ): void => {
    if (message.type === 'question') {
      if (questions.some((q) => q.question === message.question)) {
        return
      }
      setQuestions((prevValue) => {
        const tempQuestions = [
          ...prevValue,
          {
            id: message.id + new Date().toISOString(),
            name: message.clientId,
            question: message.question,
            votes: [],
            archived: false,
            deleted: false
          }
        ]
        return tempQuestions
      })
    }
    if (message.type === 'vote') {
      setQuestions((prevValue) => {
        const tempQuestions = [...prevValue]
        const objWithIdIndex = tempQuestions.findIndex(
          (obj) => obj.id === message.voteId
        )
        if (objWithIdIndex > -1) {
          tempQuestions[objWithIdIndex].votes = tempQuestions[
            objWithIdIndex
          ].votes.filter((e) => e !== message.clientId)
          if (message.voteType === 'up') {
            tempQuestions[objWithIdIndex].votes.push(message.clientId)
          }
        }
        setPreventRefresh(true)
        return tempQuestions
      })
    }
  }

  const triggerAction = (message: any, channel: any): void => {
    if (message.action !== 'updateQuestionList') {
      updateItems(message, channel)
    }
  }

  const triggerEnter = (message: any, channel: any): void => {
    setTriggerRefresh(true)
  }

  const UFDCallback = (results: any): void => {
    if (results !== false) {
      setRoomCode(results.roomCode)
      CHANNEL_GLOBAL = results.channel
    } else {
      setErrorMessage('Failed to connect, try refreshing the window')
    }
  }

  useEffect(() => {
    setRoomSettings(initialRoomSettings)
    if (!isConnecting) {
      setIsConnecting(true)
      connection('ufd', UFDCallback, { triggerAction, triggerEnter })
    }
  }, [])

  useEffect(() => {
    const newCSVData = [
      ['id', 'Question Asker', 'Question', 'Upvoted By', 'Answered', 'Deleted']
    ]
    questions.forEach((item) => {
      newCSVData.push([
        item.id,
        item.name,
        item.question,
        item.votes.join(', '),
        item.archived ? 'true' : 'false',
        item.deleted ? 'true' : 'false'
      ])
    })
    if (CHANNEL_GLOBAL !== null && !preventRefresh) {
      refreshList()
    }
    setPreventRefresh(false)
    setCsvData(newCSVData)
  }, [questions])

  useEffect(() => {
    if (CHANNEL_GLOBAL !== null) {
      refreshList()
      setTriggerRefresh(false)
    }
  }, [triggerRefresh])

  const remove = (id: string): void => {
    const tempQuestions = [...questions]
    const objWithIdIndex = tempQuestions.findIndex((obj) => obj.id === id)
    if (objWithIdIndex > -1) {
      tempQuestions[objWithIdIndex].deleted =
        !tempQuestions[objWithIdIndex].deleted
      setQuestions(tempQuestions)
    }
  }

  const toggleArchive = (id: string): void => {
    const tempQuestions = [...questions]
    const objWithIdIndex = tempQuestions.findIndex((obj) => obj.id === id)
    if (objWithIdIndex > -1) {
      tempQuestions[objWithIdIndex].archived =
        !tempQuestions[objWithIdIndex].archived
      setQuestions(tempQuestions)
    }
  }

  const setFeaturedQuestion = (id: string): void => {
    const tempQuestions = [...questions]
    const objWithIdIndex = tempQuestions.findIndex((obj) => obj.id === id)
    setFeaturedQuestionIndex(objWithIdIndex)
  }

  return (
    <Box sx={styles.container}>
      <Box ref={parent} pb="300px">
        {[...questions]
          .sort((a: itemData, b: itemData): number => {
            return a.votes.length +
              (a.archived ? -9999 : 0) +
              (a.deleted ? -999999 : 0) <
              b.votes.length +
                (b.archived ? -9999 : 0) +
                (b.deleted ? -999999 : 0)
              ? 1
              : -1
          })
          .map((item: itemData, index: number): ReactElement => {
            if (
              roomSettings?.options.hideAnsweredQuestions.value === true &&
              item.archived
            ) {
              return <></>
            }
            if (
              roomSettings?.options.showDeletedQuestions.value === false &&
              item.deleted
            ) {
              return <></>
            }
            return (
              <Box
                w="100%"
                key={item.id}
                outline={'1px solid currentcolor'}
                color={item.deleted ? 'red' : 'currentcolor'}
                borderRadius="0"
                background="yellow.300"
                mb="10px"
                px="0px"
                opacity={item.archived ? '0.5' : 1}
              >
                <Flex justifyContent="space-between" w="100%">
                  <Flex
                    as="button"
                    onClick={() => {
                      setFeaturedQuestion(item.id)
                    }}
                    alignItems="center"
                    fontSize="32px"
                    p="20px"
                    w="3em"
                    minW="3em"
                    textAlign="right"
                  >
                    {item.votes.length}
                    <ArrowUpIcon />
                  </Flex>
                  <Text
                    as="button"
                    mb="0"
                    flexGrow="1"
                    py="20px"
                    fontSize="1.5vw"
                    textAlign="left"
                    onClick={() => {
                      setFeaturedQuestion(item.id)
                    }}
                  >
                    {item.question}{' '}
                    {roomSettings?.options.hideQuestionName.value === false && (
                      <> - {item.name}</>
                    )}
                  </Text>
                  <Box py="20px" pr="20px">
                    <Button
                      variant="outlineNegative"
                      className={item.archived ? 'active' : ''}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        toggleArchive(item.id)
                      }}
                    >
                      <CheckIcon />
                    </Button>
                    &nbsp;
                    <Button
                      variant="outlineNegative"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        remove(item.id)
                      }}
                    >
                      {item.deleted ? <>&nbsp;Restore</> : <DeleteIcon />}
                    </Button>
                  </Box>
                </Flex>
              </Box>
            )
          })}
      </Box>
      <Modal
        size="4xl"
        isOpen={featuredQuestionIndex !== null}
        onClose={() => {
          setFeaturedQuestionIndex(null)
        }}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading>Question</Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex justifyContent="space-between" w="100%">
              <Box>
                {featuredQuestionIndex !== null && (
                  <Text fontSize="3vw">
                    {questions[featuredQuestionIndex].question}
                  </Text>
                )}
                {featuredQuestionIndex !== null &&
                  roomSettings?.options.hideQuestionName.value === false && (
                    <Text fontSize="2vw">
                      {' '}
                      - {questions[featuredQuestionIndex].name}
                    </Text>
                  )}
                {featuredQuestionIndex !== null && (
                  <Flex pr="20px" py="20px">
                    <Flex
                      alignItems="center"
                      justifyContent="center"
                      fontSize="32px"
                      maxW="4em"
                      minW="2em"
                      textAlign="right"
                      border="2px solid currentcolor"
                      mr="20px"
                      pl="0.25em"
                    >
                      {featuredQuestionIndex !== null &&
                        questions[featuredQuestionIndex].votes.length}
                      <ArrowUpIcon />
                    </Flex>
                    {roomSettings?.options.hideVoteNames.value === false &&
                      questions[featuredQuestionIndex].votes.length > 0 && (
                        <Text fontSize="1vw">
                          {' '}
                          <strong>Upvoted by</strong> <br />{' '}
                          {questions[featuredQuestionIndex].votes.join(', ')}
                        </Text>
                      )}
                  </Flex>
                )}
              </Box>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button
              variant="outlineNegative"
              mr={3}
              onClick={() => {
                if (featuredQuestionIndex !== null) {
                  toggleArchive(questions[featuredQuestionIndex].id)
                }
              }}
            >
              Mark as{' '}
              {featuredQuestionIndex !== null &&
              questions[featuredQuestionIndex]?.archived
                ? 'Unanswered'
                : 'Answered'}
            </Button>
            <Button
              variant="outlineNegative"
              onClick={() => {
                if (featuredQuestionIndex !== null) {
                  remove(questions[featuredQuestionIndex].id)
                  setFeaturedQuestionIndex(null)
                }
              }}
            >
              {featuredQuestionIndex !== null &&
              questions[featuredQuestionIndex]?.deleted ? (
                <>&nbsp;Restore</>
              ) : (
                <DeleteIcon />
              )}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ErrorModal
        isOpen={errorMessage !== null}
        onClose={() => {
          setErrorMessage(null)
        }}
      >
        {errorMessage}
      </ErrorModal>
      <ScreenSizeErrorModal />

      <RoomCode roomCode={roomCode} />
      <SideNav
        download={{
          data: csvData,
          filename: `up-for-discussion-${
            new Date().toISOString().split('T')[0]
          }.csv`
        }}
      />
    </Box>
  )
}

const styles = {
  container: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    p: '20px'
  },
  grid: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  }
}

export { Page }
